import React, { useState, useEffect, useMemo } from "react";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../../../services/EventsService";
import { useSelector, useDispatch } from "react-redux";
import {
  setSubscriptionDetails,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { getCouponPrice } from "../../../services/PublicServices";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../../../services/AthleteService";
import { registerCoach } from "../../../services/CoachService";
import { PostPayment } from "../../../services/PaymentService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

function PaymentOption() {
  const apiInstance = useAxiosInstance();
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState("monthly");
  const [showPayWidget, setShowPayWidget] = useState(false);
  const [spotRegPricing, setSpotRegPricing] = useState({
    combinePrice: 0,
    annualPrice: 0,
    combinePosDrillPrice: 0,
    monthlyPrice: 0,
  });
  const [adminPricing, setAdminPricing] = useState({
    monthlyPrice: 0,
    annualPrice: 0,
    combinePrice: 0,
  });
  const [couponCode, setCouponCode] = useState("");
  const getDefaultDiscountState = () => ({
    amount: 0,
    baseAmount: 0,
    percentage: 0,
    discountType: "",
    couponType: "",
  });

  const [discountInfo, setDiscountInfo] = useState(getDefaultDiscountState);
  const [couponError, setCouponError] = useState("");
  const [isRegisteringFree, setIsRegisteringFree] = useState(false);
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);

  const dispatch = useDispatch();
  const spotRegPayOptions = useMemo(() => {
    return JSON.parse(localStorage.getItem("subscriptionDetails") || "{}");
  }, []);
  const storedData = JSON.parse(
    localStorage.getItem("registeredAccount") || "{}",
  );
  const accounts = useSelector((state) => state.account.register);
  const user = accounts.length ? accounts[0] : storedData;
  const navigate = useNavigate();
  const [payWidgetVersion, setPayWidgetVersion] = useState(0);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (!accounts.length && storedData) {
      dispatch(registerAccountStore(storedData));
    }
  }, [dispatch, accounts.length, storedData]);

  useEffect(() => {
    const fetchPrices = async () => {
      let userPricing = "";
      let combineData = "";
      try {
        setIsLoading(true);
        if (user.eventId) {
          combineData = await GetEventCombinePrice(apiInstance, user.eventId);
        }

        const adminPrices = await GetAdminPrice(apiInstance);

        const athletePricing = adminPrices?.data?.find(
          (option) => option.role.toLowerCase() === "athlete",
        );

        if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
          setSpotRegPricing({
            combinePrice: combineData?.data?.combinePrice ?? 0,
            annualPrice: athletePricing?.pricingOptions?.annualPrice ?? 0,
            combinePosDrillPrice: combineData?.data?.positionDrillPrice ?? 0,
            monthlyPrice: athletePricing?.pricingOptions?.monthlyPrice ?? 0,
          });
          trackEvent("Registration3_ViewedPricing", {
            event_id: user.eventId,
            combine_price: combineData?.data?.combinePrice ?? 0,
          });
          setShowPayWidget(true);
        } else {
          userPricing = adminPrices.data.find(
            (option) => option.role.toLowerCase() === user.role.toLowerCase(),
          );
        }

        if (userPricing) {
          setAdminPricing(userPricing.pricingOptions);
          setShowPayWidget(true);
        }
      } catch (error) {
        console.error("Error fetching pricing:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, [apiInstance, spotRegPayOptions, user.eventId, user.role, trackEvent]);

  const basePrice =
    spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0
      ? (spotRegPayOptions.combine ? spotRegPricing.combinePrice : 0) +
        (spotRegPayOptions.positionDrill
          ? spotRegPricing.combinePosDrillPrice
          : 0) +
        (spotRegPayOptions.subscription ? spotRegPricing.annualPrice : 0)
      : subscriptionType === "monthly"
        ? adminPricing.monthlyPrice
        : adminPricing.annualPrice;

  const selectedPrice = Math.max(
    basePrice - Number(discountInfo.amount || 0),
    0,
  );

  const handleToggle = (type) => {
    setSubscriptionType(type);
    setShowPayWidget(true);
  };

  const finalSubscriptionType = useMemo(() => {
    if (spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0) {
      const result = [];
      if (spotRegPayOptions.combine) result.push("combine");
      if (spotRegPayOptions.positionDrill) result.push("positionDrill");
      if (spotRegPayOptions.subscription) result.push("subscription");
      return result;
    } else {
      return subscriptionType;
    }
  }, [spotRegPayOptions, subscriptionType]);

  const staticUser = useMemo(
    () => ({
      email: user?.email,
      id: user.id,
      price: selectedPrice,
      eventId: user.eventId,
    }),
    [user?.email, user.id, selectedPrice, user.eventId],
  );

  useEffect(() => {
    if (staticUser) {
      dispatch(
        setSubscriptionDetails({
          subscriptionType: finalSubscriptionType,
          subscriptionPrice: staticUser.price,
        }),
      );
    }
  }, [dispatch, finalSubscriptionType, staticUser]);

  const isSpot = spotRegPayOptions && Object.keys(spotRegPayOptions).length > 0;
  const combineSelected = Boolean(spotRegPayOptions?.combine);
  const positionSelected = Boolean(spotRegPayOptions?.positionDrill);
  const subscriptionSelected = Boolean(spotRegPayOptions?.subscription);

  const combinePrice = combineSelected
    ? Number(spotRegPricing.combinePrice ?? 0)
    : 0;
  const positionDrillPrice = positionSelected
    ? Number(spotRegPricing.combinePosDrillPrice ?? 0)
    : 0;

  const spotSubscriptionPrice = subscriptionSelected
    ? Number(spotRegPricing.annualPrice ?? 0)
    : 0;

  const normalSubscriptionPrice =
    subscriptionType === "monthly"
      ? Number(adminPricing.monthlyPrice ?? 0)
      : Number(adminPricing.annualPrice ?? 0);

  const couponNotApplicableMessage = (couponType) => {
    switch (couponType) {
      case "Combine":
        return "This coupon applies to Combine only. Please select a Combine Pass to use it.";

      case "PositionDrills":
        return "This coupon applies to Position Drills only. Please select Position Drills to use it.";

      case "CombineAndPositionDrills":
        return "This coupon applies to Combine and/or Position Drills. Please select at least one to use it.";

      case "Subscription":
        return "This coupon applies to Subscription only (Monthly or Annual). Please choose a subscription plan to use it.";

      default:
        return "This coupon is not applicable to your current selection.";
    }
  };

  const validateCouponApplicability = (couponType) => {
    if (!isSpot) {
      if (couponType !== "Subscription") {
        return {
          ok: false,
          eligibleAmount: 0,
          message:
            "This coupon can only be applied during Combine/Position-Drills checkout.",
        };
      }

      const eligible = normalSubscriptionPrice;
      if (!eligible || eligible <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Subscription"),
        };
      }
      return { ok: true, eligibleAmount: eligible };
    }

    if (couponType === "Combine") {
      if (!combineSelected || combinePrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Combine"),
        };
      }
      return { ok: true, eligibleAmount: combinePrice };
    }

    if (couponType === "PositionDrills") {
      if (!positionSelected || positionDrillPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("PositionDrills"),
        };
      }
      return { ok: true, eligibleAmount: positionDrillPrice };
    }

    if (couponType === "CombineAndPositionDrills") {
      const eligibleAmount = combinePrice + positionDrillPrice;
      if (eligibleAmount <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("CombineAndPositionDrills"),
        };
      }
      return { ok: true, eligibleAmount };
    }

    if (couponType === "Subscription") {
      if (!subscriptionSelected || spotSubscriptionPrice <= 0) {
        return {
          ok: false,
          eligibleAmount: 0,
          message: couponNotApplicableMessage("Subscription"),
        };
      }
      return { ok: true, eligibleAmount: spotSubscriptionPrice };
    }

    return {
      ok: false,
      eligibleAmount: 0,
      message: couponNotApplicableMessage(couponType),
    };
  };

  const applyCouponCode = async () => {
    try {
      const response = await getCouponPrice(apiInstance, couponCode);

      const amount = Number(response?.amount ?? 0);
      const couponType = String(
        response?.type || response?.couponType || "",
      ).trim();
      const discountType = String(response?.discountType || "").trim();

      if (!amount || amount <= 0) {
        setCouponError("The coupon code is invalid or does not apply.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      if (!couponType) {
        setCouponError("Coupon type is missing. Please contact support.");
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      const { ok, eligibleAmount, message } =
        validateCouponApplicability(couponType);

      if (!ok) {
        setCouponError(
          message || "This coupon is not applicable to your current selection.",
        );
        setDiscountInfo(getDefaultDiscountState());
        return;
      }

      let appliedBaseAmount = eligibleAmount;
      let appliedDiscountAmount = 0;
      let appliedPercentage = 0;

      // Combine + Percent Off => calculate only on combine base price
      if (couponType === "Combine" && discountType === "Percent Off") {
        appliedBaseAmount = combinePrice;
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (combinePrice * amount) / 100,
          combinePrice,
        );
      }
      // Combine + Amount Off => calculate on eligible/base amount
      else if (couponType === "Combine" && discountType === "Amount Off") {
        appliedBaseAmount = eligibleAmount;
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      }
      // CombineAndPositionDrills + Percent Off => calculate on total
      else if (
        couponType === "CombineAndPositionDrills" &&
        discountType === "Percent Off"
      ) {
        appliedBaseAmount = eligibleAmount;
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      }
      // Generic fallback for other coupon types
      else if (discountType === "Percent Off") {
        appliedBaseAmount = eligibleAmount;
        appliedPercentage = amount;
        appliedDiscountAmount = Math.min(
          (appliedBaseAmount * amount) / 100,
          appliedBaseAmount,
        );
      } else {
        appliedBaseAmount = eligibleAmount;
        appliedDiscountAmount = Math.min(amount, appliedBaseAmount);
      }

      const nextDiscountInfo = {
        amount: appliedDiscountAmount,
        baseAmount: appliedBaseAmount,
        percentage: appliedPercentage,
        discountType,
        couponType,
      };

      const isSameDiscount =
        Number(discountInfo.amount) === Number(nextDiscountInfo.amount) &&
        Number(discountInfo.baseAmount) ===
          Number(nextDiscountInfo.baseAmount) &&
        Number(discountInfo.percentage) ===
          Number(nextDiscountInfo.percentage) &&
        discountInfo.discountType === nextDiscountInfo.discountType &&
        discountInfo.couponType === nextDiscountInfo.couponType;

      if (isSameDiscount) {
        setCouponError("");
        localStorage.setItem("appliedCouponCode", couponCode);
        localStorage.setItem("appliedCouponType", couponType);
        return;
      }

      setDiscountInfo(nextDiscountInfo);
      setCouponError("");

      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: user.eventId,
        coupon_code: couponCode,
        coupon_type: couponType,
        discount_amount: nextDiscountInfo.amount,
      });

      setIsIntellipayReady(false);
      setShowPayWidget(true);
      setPayWidgetVersion((v) => v + 1);

      localStorage.setItem("appliedCouponCode", couponCode);
      localStorage.setItem("appliedCouponType", couponType);
    } catch (error) {
      console.error("Invalid coupon code", error);
      setCouponError("Invalid coupon code");
      setDiscountInfo(getDefaultDiscountState());
    }
  };

  useEffect(() => {
    if (discountInfo.amount > 0) {
      const couponType = localStorage.getItem("appliedCouponType") || "";
      if (couponType) {
        const { ok } = validateCouponApplicability(couponType);
        if (!ok) {
          setDiscountInfo(getDefaultDiscountState());
          setCouponError(
            "Selection changed — coupon removed because it no longer applies.",
          );
          localStorage.removeItem("appliedCouponCode");
          localStorage.removeItem("appliedCouponType");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spotRegPayOptions,
    subscriptionType,
    spotRegPricing,
    adminPricing,
    discountInfo.amount,
  ]);

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
  };

  const handleRegister = async () => {
    const currentUser = user;
    let newUserId = currentUser.id;
    setIsRegisteringFree(true);

    try {
      if (!currentUser.registered) {
        if (currentUser.role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Athlete registration failed.");
        } else if (currentUser.role === "university") {
          const reg = await registerCoach(apiInstance, currentUser);
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Coach registration failed.");
        } else if (currentUser.role === "spotcombine") {
          const reg = await registerAthleteWithEventId(
            apiInstance,
            currentUser,
            currentUser.eventId,
          );
          if (reg.status === 200) newUserId = reg.data.id;
          else throw new Error("Spot combine registration failed.");
        }
      }
      let paymentPayloads = [];

      if (spotRegPayOptions?.combine) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: true,
          isPositionDrill: false,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.positionDrill) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: true,
          isAnnualSubscription: false,
          isMonthlySubscription: false,
          isCash: false,
          eventId: currentUser.eventId,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (spotRegPayOptions?.subscription) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: true,
          isMonthlySubscription: false,
          isCash: false,
          eventId: 0,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      if (!spotRegPayOptions || Object.keys(spotRegPayOptions).length === 0) {
        paymentPayloads.push({
          userId: newUserId,
          amount: 0,
          isCombine: false,
          isPositionDrill: false,
          isAnnualSubscription: subscriptionType === "annual",
          isMonthlySubscription: subscriptionType === "monthly",
          isCash: false,
          eventId: 0,
          transactionId: "N/A",
          intellipayToken: "",
        });
      }

      for (const payload of paymentPayloads) {
        const paid = await PostPayment(apiInstance, payload);
        if (paid.status !== 200) {
          throw new Error("Payment failed.");
        }
      }

      const couponCode = localStorage.getItem("appliedCouponCode");
      if (couponCode) {
        await postAppliedCouponCode(couponCode);
      }

      trackEvent("Registration5_RegistrationCompleted", {
        event_id: user.eventId,
        payment_method: "free",
      });
      toast.success("Registration and payment successful!", {
        position: "top-right",
      });
      setTimeout(() => {
        navigate("/register/registrationsuccess");
      }, 500);
    } catch (error) {
      trackEvent("Registration4_5_PaymentFailed", {
        event_id: user.eventId,
        reason: error.message ?? "unknown",
      });
      toast.error(error.message || "Registration or payment failed", {
        position: "top-right",
      });
    } finally {
      setIsRegisteringFree(false);
    }
  };

  useEffect(() => {
    if (selectedPrice === 0) {
      setShowPayWidget(false);
      setIsIntellipayReady(false);
    } else {
      setShowPayWidget(true);
    }
  }, [selectedPrice]);

  return (
    <>
      <div className="flex items-center justify-center mt-6">
        <div className="w-full max-w-4xl px-3">
          <div className="text-center text-white uppercase text-2xl xl:text-3xl lg:text-3xl font-black italic">
            Simple, Transparent Pricing
          </div>
          <p className="text-center text-white text-base font-normal py-2">
            Clear, straightforward pricing with no hidden fees.
          </p>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-4xl text-white"
              />
            </div>
          ) : (
            <>
              <div className="mt-6 grid justify-items-center">
                {(!spotRegPayOptions ||
                  Object.keys(spotRegPayOptions).length === 0) && (
                  <div className="price-top flex gap-4">
                    <button
                      onClick={() => handleToggle("monthly")}
                      className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "monthly" ? "bg-red-500" : "bg-gray-600"}`}
                    >
                      Monthly
                    </button>

                    <button
                      onClick={() => handleToggle("annual")}
                      className={`text-white px-16 py-4 rounded-full block uppercase cursor-pointer transition duration-300 ${subscriptionType === "annual" ? "bg-red-500" : "bg-gray-600"}`}
                    >
                      Annual
                    </button>
                  </div>
                )}

                <div className="w-96 xl:w-96 lg:w-96 m-auto mt-6">
                  <div className="rounded-2xl border border-white-600">
                    <div className="flex flex-col px-6 py-4 text-white space-y-3">
                      {spotRegPayOptions &&
                      Object.keys(spotRegPayOptions).length > 0 ? (
                        <>
                          {spotRegPayOptions.combine && (
                            <div className="flex justify-between">
                              <span>Combine Pass</span>
                              <span>
                                ${(spotRegPricing.combinePrice ?? 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {spotRegPayOptions.positionDrill && (
                            <div className="flex justify-between">
                              <span>Position Drills</span>
                              <span>
                                $
                                {(
                                  spotRegPricing.combinePosDrillPrice ?? 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {spotRegPayOptions.subscription && (
                            <div className="flex justify-between">
                              <span>Annual Subscription</span>
                              <span>
                                ${(spotRegPricing.annualPrice ?? 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span>
                              {subscriptionType === "monthly"
                                ? "Monthly Subscription"
                                : "Annual Subscription"}
                            </span>
                            <span>
                              $
                              {(subscriptionType === "monthly"
                                ? (adminPricing.monthlyPrice ?? 0)
                                : (adminPricing.annualPrice ?? 0)
                              ).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}

                      {discountInfo.amount > 0 && (
                        <>
                          <div className="flex justify-between text-green-400 font-medium text-sm mt-2">
                            <span>Discount Base Amount</span>
                            <span>
                              ${Number(discountInfo.baseAmount || 0).toFixed(2)}
                            </span>
                          </div>

                          {discountInfo.discountType === "Percent Off" && (
                            <>
                              {/* <div className="flex justify-between text-green-400 font-medium text-sm">
                              <span>Coupon Type</span>
                              <span>{discountInfo.couponType}</span>
                            </div> */}
                              <div className="flex justify-between text-green-400 font-medium text-sm">
                                <span>
                                  Discount Percentage of{" "}
                                  {discountInfo.couponType} is{" "}
                                </span>
                                <span>
                                  {Number(discountInfo.percentage || 0).toFixed(
                                    2,
                                  )}
                                  %
                                </span>
                              </div>
                            </>
                          )}

                          <div className="flex justify-between text-green-400 font-medium text-sm">
                            <span>Discount Amount</span>
                            <span>
                              - ${Number(discountInfo.amount || 0).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                      <div className="border-t border-white-600 my-2"></div>
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total:</span>
                        <span>${(selectedPrice ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid justify-items-center">
                <div className="w-92 xl:w-96 lg:w-96 m-auto mt-4 px-6 text-white">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="coupon" className="text-sm font-semibold">
                      Coupon Code:
                    </label>
                    <input
                      type="text"
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      className="text-black px-2 py-1 rounded"
                    />
                    <button
                      onClick={applyCouponCode}
                      className="bg-white text-black px-3 py-1 rounded hover:bg-gray-200"
                    >
                      Apply
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 mt-2 text-sm">{couponError}</p>
                  )}
                </div>

                <div className="grid justify-items-center">
                  {selectedPrice === 0 ? (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleRegister}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleRegister();
                        }
                      }}
                      className="bg-green-500 mt-4 text-white uppercase rounded px-12 py-2 hover:bg-green-700 cursor-pointer text-center"
                    >
                      Register
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={`mt-4 text-white uppercase rounded px-12 py-2 ipayfield
                        ${isIntellipayReady ? "bg-red-500 hover:bg-red-700 cursor-pointer" : "bg-gray-500 cursor-not-allowed opacity-70"}`}
                      data-ipayname="submit"
                      disabled={!isIntellipayReady}
                      aria-busy={!isIntellipayReady}
                      onClick={(e) => {
                        if (!isIntellipayReady) {
                          e.preventDefault();
                          toast.info("IntelliPay is loading… please wait.", {
                            position: "top-right",
                          });
                          return;
                        }
                      }}
                    >
                      {isIntellipayReady ? "Pay" : "Loading…"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {showPayWidget && selectedPrice > 0 && !isRegisteringFree && (
          <PayWidgetEvents
            key={`paywidget-${payWidgetVersion}-${selectedPrice}`}
            userEmail={staticUser.email}
            userId={staticUser.id}
            paymentAmount={staticUser.price}
            eventId={staticUser.eventId}
            subscriptionType={finalSubscriptionType}
            onIntellipayReady={setIsIntellipayReady}
          />
        )}
      </div>
    </>
  );
}

export default PaymentOption;
